<!--
/**
 * FormKitOptionCards
 *
 * A choice shown as side-by-side cards, each with a one-line description, so
 * every option is visible without opening a dropdown.
 *
 * FormKit usage:
 *   <FormKit type="optionCards" name="registrationMode" :cards="cards" aria-label="Registration mode" />
 *
 * @prop {OptionCard[]} cards - The options; see types/option-cards.ts.
 */
-->
<template>
  <div
    role="radiogroup"
    :aria-label="context.attrs['aria-label']"
    class="grid gap-3 sm:grid-cols-2"
    :data-testid="`option-cards-${context.node.name}`"
  >
    <div
      v-for="card in cards"
      :key="card.value"
      class="flex flex-col gap-3 rounded-lg border p-3.5 transition-colors"
      :class="
        card.value === context._value
          ? 'border-primary-6 bg-primary-0 ring-1 ring-primary-6'
          : 'border-fp-gray-3 bg-white hover:border-fp-gray-4'
      "
    >
      <label
        class="flex flex-col gap-3"
        :class="disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'"
      >
        <span class="flex items-start gap-2.5">
          <input
            type="radio"
            class="mt-0.5 size-4 shrink-0 accent-fp-green-6"
            :name="context.node.name"
            :value="card.value"
            :checked="card.value === context._value"
            :disabled="disabled"
            @change="context.node.input(card.value)"
          />
          <span>
            <span class="block text-sm font-semibold text-fp-gray-6">{{
              card.label
            }}</span>
            <span
              class="mt-0.5 block text-xs"
              :class="
                card.value === context._value
                  ? 'text-primary-7'
                  : 'text-fp-gray-5'
              "
              >{{ card.description }}</span
            >
          </span>
        </span>
        <component :is="card.illustration" v-if="card.illustration" />
      </label>
      <div
        v-if="card.link && card.value === context._value"
        class="flex flex-wrap items-center gap-2 border-t border-primary-2 pt-2.5 text-xs"
      >
        <a
          :href="card.link"
          target="_blank"
          class="min-w-0 break-all text-primary-7 underline"
          :data-testid="`option-card-link-${card.value}`"
          >{{ card.link }}</a
        >
        <button
          type="button"
          class="rounded border border-primary-6 bg-white px-2 py-0.5 font-semibold text-primary-7"
          @click="copy(card.link)"
        >
          {{ copied ? 'Copied' : 'Copy link' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, type PropType } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'
import type { OptionCard } from '~/types/option-cards'

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & { cards?: OptionCard[] }
    >,
    required: true,
  },
})

const cards = computed(() => props.context.cards ?? [])
const disabled = computed(() => Boolean(props.context.disabled))

const copied = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | undefined

async function copy(link: string) {
  await navigator.clipboard.writeText(link)
  copied.value = true
  clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => (copied.value = false), 2000)
}

onBeforeUnmount(() => clearTimeout(copiedTimer))
</script>
